import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
// Required for FullCalendar to honor a named IANA timeZone ("Asia/Dubai").
// Without it, timeZone falls back to UTC and every event renders ~4h off.
import luxonPlugin from '@fullcalendar/luxon3';
import type { EventInput, EventClickArg, EventDropArg } from '@fullcalendar/core';
import type { EventResizeDoneArg } from '@fullcalendar/interaction';

import { BRAND } from '@fin/shared/brand';
import {
  STATUS_COLORS,
  STATUS_LABELS,
  SERVICE_LABELS,
  JOB_STATUSES,
  type JobStatus,
} from '@fin/shared/enums';

import { Card, PageHeader } from '../components/ui';
import {
  usePatchJob,
  useCustomers,
  useWorkers,
  type Job,
} from '../lib/queries';
import { api } from '../lib/api';

interface ListResponse<T> {
  items: T[];
  nextCursor?: string;
}

// Statuses we surface on the calendar (everything except draft & cancelled).
const CAL_STATUSES: JobStatus[] = JOB_STATUSES.filter(
  (s) => s !== 'draft' && s !== 'cancelled',
);

export default function CalendarPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const patch = usePatchJob();
  const customers = useCustomers();
  const workers = useWorkers();
  const [error, setError] = useState<string | null>(null);

  // Fan out one query per status partition; tanstack-query dedupes & caches.
  const queries = useQueries({
    queries: CAL_STATUSES.map((s) => ({
      queryKey: ['jobs', s],
      queryFn: () => api<ListResponse<Job>>(`/jobs?status=${s}`).then((r) => r.items),
      staleTime: 30_000,
    })),
  });

  const loading = queries.some((q) => q.isPending);
  const jobs: Job[] = useMemo(
    () => queries.flatMap((q) => q.data ?? []),
    [queries],
  );

  const customerById = useMemo(() => {
    const m = new Map<string, string>();
    (customers.data ?? []).forEach((c) => m.set(c.id, c.name));
    return m;
  }, [customers.data]);

  const workerById = useMemo(() => {
    const m = new Map<string, string>();
    (workers.data ?? []).forEach((w) => m.set(w.id, w.name));
    return m;
  }, [workers.data]);

  const events: EventInput[] = useMemo(() => {
    return jobs.map((j) => {
      const start = new Date(j.scheduledAt);
      const end = new Date(start.getTime() + (j.estimatedDurationMinutes || 60) * 60_000);
      const customerName = customerById.get(j.customerId) ?? 'Customer';
      const serviceLabel =
        SERVICE_LABELS[j.serviceType as keyof typeof SERVICE_LABELS]?.en ?? j.serviceType;
      const ids = j.assignedWorkerIds && j.assignedWorkerIds.length > 0
        ? j.assignedWorkerIds
        : (j.assignedWorkerId ? [j.assignedWorkerId] : []);
      const workerNames = ids.map((id) => workerById.get(id) ?? '?').join(', ');
      const title = workerNames
        ? `${customerName} · ${serviceLabel} · ${workerNames}`
        : `${customerName} · ${serviceLabel}`;
      return {
        id: j.id,
        title: j.anytime ? `${title} · Anytime` : title,
        // Anytime jobs have no committed slot — render all-day.
        ...(j.anytime
          ? { start: start.toISOString().slice(0, 10), allDay: true }
          : { start: start.toISOString(), end: end.toISOString() }),
        backgroundColor: STATUS_COLORS[j.status],
        borderColor: STATUS_COLORS[j.status],
        textColor: 'white',
        extendedProps: { job: j },
      };
    });
  }, [jobs, customerById, workerById]);

  function handleClick(arg: EventClickArg) {
    navigate(`/jobs/${arg.event.id}`);
  }

  async function handleDrop(arg: EventDropArg | EventResizeDoneArg) {
    const job = arg.event.extendedProps.job as Job | undefined;
    if (!job || !arg.event.start) {
      arg.revert();
      return;
    }
    const newIso = arg.event.start.toISOString();
    const body: Record<string, unknown> = { scheduledAt: newIso };
    // If user resized, also push estimatedDurationMinutes.
    if (arg.event.end && 'oldEvent' in arg) {
      const minutes = Math.round((arg.event.end.getTime() - arg.event.start.getTime()) / 60_000);
      if (minutes > 0 && minutes !== job.estimatedDurationMinutes) {
        body.estimatedDurationMinutes = minutes;
      }
    }
    try {
      await patch.mutateAsync({ id: job.id, body });
      qc.invalidateQueries({ queryKey: ['jobs'] });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      arg.revert();
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader title="Calendar" />

      {error && (
        <div
          role="alert"
          className="rounded border px-3 py-2 text-sm"
          style={{ borderColor: BRAND.danger, color: BRAND.danger, backgroundColor: '#FEF2F2' }}
        >
          {error}
        </div>
      )}

      <Card className="p-3 md:p-5">
        {loading && <p className="text-stone-500 text-sm">Loading jobs…</p>}
        <div className="rounded-md bg-white">
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, luxonPlugin]}
            timeZone="Asia/Dubai"
            initialView="timeGridWeek"
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay',
            }}
            events={events}
            editable
            eventStartEditable
            eventDurationEditable
            droppable={false}
            allDaySlot
            nowIndicator
            slotMinTime="06:00:00"
            slotMaxTime="22:00:00"
            firstDay={1}
            height="auto"
            eventClick={handleClick}
            eventDrop={handleDrop}
            eventResize={handleDrop}
          />
        </div>

        <div className="mt-4 flex flex-wrap gap-2 text-[11px]">
          {CAL_STATUSES.map((s) => (
            <span
              key={s}
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium"
              style={{ backgroundColor: STATUS_COLORS[s], color: 'white' }}
            >
              {STATUS_LABELS[s].en}
            </span>
          ))}
        </div>
      </Card>
    </div>
  );
}
